import { supabase } from "@/lib/supabase";
import { compressImage } from "@/lib/image-compression";
import { startWorkSession, type WorkSessionKind } from "@/lib/workSessions";
import type { Database } from "@/integrations/supabase/types";

export type ExtraJobOrigin = "coordinator" | "employee";
export type ExtraJobPayMode = "hourly" | "fixed";
export type ExtraJobApprovalStatus = "pending_review" | "approved" | "rejected";
export type ExtraJobPhotoSource = "camera" | "gallery";
export type { WorkSessionKind };

export interface ExtraJobValidation {
  gpsRequired: boolean;
  qrRequired: boolean;
  biometricRequired: boolean;
  requiresPhoto: boolean;
}

export interface ExtraJobRates {
  payMode: ExtraJobPayMode;
  employeeHourlyRate: number | null;
  clientHourlyRate: number | null;
  employeeFixedAmount: number | null;
  clientFixedAmount: number | null;
}

export interface CleaningExtraJob {
  id: string;
  orgId: string;
  taskId: string;
  locationId: string;
  assignedStaffId: string | null;
  createdBy: string;
  origin: ExtraJobOrigin;
  title: string;
  instructions: string | null;
  workReport: string | null;
  validation: ExtraJobValidation;
  rates: ExtraJobRates;
  appliedEmployeeRate: number | null;
  appliedClientRate: number | null;
  employeeTotal: number | null;
  clientTotal: number | null;
  durationMinutes: number | null;
  approvalStatus: ExtraJobApprovalStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  taskStatus: string | null;
  startedAt: string | null;
  locationAddress?: string | null;
  staffName?: string | null;
}

export interface CleaningExtraJobPhoto {
  id: string;
  extraJobId: string;
  storagePath: string;
  source: ExtraJobPhotoSource;
  sortOrder: number;
  createdAt: string;
}

type ExtraJobRow = Database["public"]["Tables"]["cleaning_extra_jobs"]["Row"];
type ExtraPhotoRow = Database["public"]["Tables"]["cleaning_extra_job_photos"]["Row"];

const EXTRA_JOB_SELECT =
  "id, org_id, task_id, location_id, assigned_staff_id, created_by, origin, title, instructions, work_report, requires_photo, gps_required, qr_required, biometric_required, pay_mode, employee_hourly_rate, client_hourly_rate, employee_fixed_amount, client_fixed_amount, applied_employee_rate, applied_client_rate, employee_total, client_total, duration_minutes, approval_status, approved_by, approved_at, rejection_reason, created_at, cleaning_tasks(status, started_at), cleaning_locations(address), profiles!cleaning_extra_jobs_assigned_staff_id_fkey(full_name)";

export function isExtraPaidTask(task: { task_type?: string | null }): boolean {
  return task.task_type === "extra_paid";
}

export function extraJobNeedsPayroll(job: Pick<CleaningExtraJob, "approvalStatus" | "employeeTotal">): boolean {
  return job.approvalStatus === "approved" && job.employeeTotal != null && job.employeeTotal > 0;
}

function firstJoined<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function asPayMode(value: string | null | undefined): ExtraJobPayMode {
  return value === "fixed" ? "fixed" : "hourly";
}

function asOrigin(value: string | null | undefined): ExtraJobOrigin {
  return value === "coordinator" ? "coordinator" : "employee";
}

function asApproval(value: string | null | undefined): ExtraJobApprovalStatus {
  if (value === "approved" || value === "rejected") return value;
  return "pending_review";
}

export function mapExtraJob(row: ExtraJobRow & {
  cleaning_tasks?: { status: string | null; started_at: string | null } | { status: string | null; started_at: string | null }[] | null;
  cleaning_locations?: { address: string | null } | { address: string | null }[] | null;
  profiles?: { full_name: string | null } | { full_name: string | null }[] | null;
}): CleaningExtraJob {
  const task = firstJoined(row.cleaning_tasks);
  const location = firstJoined(row.cleaning_locations);
  const staff = firstJoined(row.profiles);
  return {
    id: row.id,
    orgId: row.org_id,
    taskId: row.task_id,
    locationId: row.location_id,
    assignedStaffId: row.assigned_staff_id,
    createdBy: row.created_by,
    origin: asOrigin(row.origin),
    title: row.title,
    instructions: row.instructions,
    workReport: row.work_report,
    validation: {
      gpsRequired: row.gps_required,
      qrRequired: row.qr_required,
      biometricRequired: row.biometric_required,
      requiresPhoto: row.requires_photo,
    },
    rates: {
      payMode: asPayMode(row.pay_mode),
      employeeHourlyRate: row.employee_hourly_rate,
      clientHourlyRate: row.client_hourly_rate,
      employeeFixedAmount: row.employee_fixed_amount,
      clientFixedAmount: row.client_fixed_amount,
    },
    appliedEmployeeRate: row.applied_employee_rate,
    appliedClientRate: row.applied_client_rate,
    employeeTotal: row.employee_total,
    clientTotal: row.client_total,
    durationMinutes: row.duration_minutes,
    approvalStatus: asApproval(row.approval_status),
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    rejectionReason: row.rejection_reason,
    createdAt: row.created_at,
    taskStatus: task?.status ?? null,
    startedAt: task?.started_at ?? null,
    locationAddress: location?.address ?? null,
    staffName: staff?.full_name ?? null,
  };
}

export function rpcErrorMessage(error: { message?: string } | null | undefined, fallback: string): string {
  const msg = (error?.message ?? "").toLowerCase();
  if (msg.includes("work report")) return "Opis wykonanej pracy jest wymagany.";
  if (msg.includes("photo is required")) return "Wymagane jest co najmniej jedno zdjęcie.";
  if (msg.includes("has not been started")) return "Najpierw rozpocznij pracę ekstra.";
  if (msg.includes("fixed extra jobs")) return "Przy kwocie stałej podaj wynagrodzenie pracownika.";
  if (msg.includes("title is required")) return "Podaj nazwę zlecenia.";
  if (msg.includes("only coordinators")) return "Tylko koordynator lub właściciel może to zrobić.";
  if (msg.includes("not allowed")) return "Brak uprawnień do tej operacji.";
  if (msg.includes("rejected")) return "Odrzuconego zlecenia nie można zakończyć.";
  return error?.message || fallback;
}

export async function fetchStaffExtraJobLocationIds(staffUserId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("cleaning_extra_jobs")
    .select("location_id")
    .eq("assigned_staff_id", staffUserId)
    .neq("approval_status", "rejected");
  if (error) {
    console.error("[fetchStaffExtraJobLocationIds]", error);
    throw error;
  }
  return [...new Set((data ?? []).map((row) => row.location_id).filter(Boolean))];
}

export async function fetchLocationExtraJobs(locationId: string, staffUserId: string): Promise<CleaningExtraJob[]> {
  const { data, error } = await supabase
    .from("cleaning_extra_jobs")
    .select(EXTRA_JOB_SELECT)
    .eq("location_id", locationId)
    .eq("assigned_staff_id", staffUserId)
    .neq("approval_status", "rejected")
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[fetchLocationExtraJobs]", error);
    throw error;
  }
  return (data ?? []).map((row) => mapExtraJob(row as ExtraJobRow));
}

export async function fetchOrgExtraJobs(orgId: string): Promise<CleaningExtraJob[]> {
  const { data, error } = await supabase
    .from("cleaning_extra_jobs")
    .select(EXTRA_JOB_SELECT)
    .eq("org_id", orgId)
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) {
    console.error("[fetchOrgExtraJobs]", error);
    throw error;
  }
  return (data ?? []).map((row) => mapExtraJob(row as ExtraJobRow));
}

export async function fetchExtraJobPhotos(jobId: string): Promise<CleaningExtraJobPhoto[]> {
  const { data, error } = await supabase
    .from("cleaning_extra_job_photos")
    .select("id, extra_job_id, storage_path, source, sort_order, created_at")
    .eq("extra_job_id", jobId)
    .order("sort_order", { ascending: true });
  if (error) {
    console.error("[fetchExtraJobPhotos]", error);
    throw error;
  }
  return (data ?? []).map((row: ExtraPhotoRow) => ({
    id: row.id,
    extraJobId: row.extra_job_id,
    storagePath: row.storage_path,
    source: row.source === "gallery" ? "gallery" : "camera",
    sortOrder: row.sort_order,
    createdAt: row.created_at,
  }));
}

export async function createCoordinatorExtraJob(input: {
  orgId: string;
  locationId: string;
  assignedStaffId: string;
  title: string;
  instructions?: string;
  requiresPhoto: boolean;
  gpsRequired: boolean;
  qrRequired: boolean;
  biometricRequired: boolean;
  payMode: ExtraJobPayMode;
  employeeHourlyRate?: number | null;
  clientHourlyRate?: number | null;
  employeeFixedAmount?: number | null;
  clientFixedAmount?: number | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc("create_cleaning_extra_job", {
    p_org_id: input.orgId,
    p_location_id: input.locationId,
    p_assigned_staff_id: input.assignedStaffId,
    p_title: input.title,
    p_instructions: input.instructions ?? undefined,
    p_requires_photo: input.requiresPhoto,
    p_gps_required: input.gpsRequired,
    p_qr_required: input.qrRequired,
    p_biometric_required: input.biometricRequired,
    p_pay_mode: input.payMode,
    p_employee_hourly_rate: input.employeeHourlyRate ?? undefined,
    p_client_hourly_rate: input.clientHourlyRate ?? undefined,
    p_employee_fixed_amount: input.employeeFixedAmount ?? undefined,
    p_client_fixed_amount: input.clientFixedAmount ?? undefined,
  });
  if (error) throw new Error(rpcErrorMessage(error, "Nie udało się dodać zlecenia ekstra."));
  return data as string;
}

export async function startEmployeeExtraJob(input: {
  orgId: string;
  locationId: string;
  userId: string;
  title?: string;
  latitude: number | null;
  longitude: number | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc("start_employee_extra_job", {
    p_org_id: input.orgId,
    p_location_id: input.locationId,
    p_title: input.title ?? "Praca ekstra",
  });
  if (error) throw new Error(rpcErrorMessage(error, "Nie udało się rozpocząć pracy ekstra."));
  const jobId = data as string;
  const { data: job, error: jobError } = await supabase
    .from("cleaning_extra_jobs")
    .select("task_id")
    .eq("id", jobId)
    .single();
  if (jobError || !job) throw new Error("Nie znaleziono utworzonego zlecenia.");
  await startWorkSession({
    orgId: input.orgId,
    locationId: input.locationId,
    userId: input.userId,
    taskId: job.task_id,
    latitude: input.latitude,
    longitude: input.longitude,
    sessionKind: "extra_paid",
  });
  return jobId;
}

export async function startAssignedExtraJob(input: {
  job: CleaningExtraJob;
  userId: string;
  latitude: number | null;
  longitude: number | null;
}): Promise<void> {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from("cleaning_tasks")
    .update({ status: "in_progress", started_at: now })
    .eq("id", input.job.taskId);
  if (error) throw new Error("Nie udało się rozpocząć zlecenia ekstra.");
  await startWorkSession({
    orgId: input.job.orgId,
    locationId: input.job.locationId,
    userId: input.userId,
    taskId: input.job.taskId,
    latitude: input.latitude,
    longitude: input.longitude,
    sessionKind: "extra_paid",
  });
}

export async function completeExtraJob(jobId: string, workReport: string): Promise<void> {
  const { error } = await supabase.rpc("complete_cleaning_extra_job", {
    p_job_id: jobId,
    p_work_report: workReport,
  });
  if (error) throw new Error(rpcErrorMessage(error, "Nie udało się zakończyć pracy ekstra."));
}

export async function approveExtraJob(input: {
  jobId: string;
  payMode: ExtraJobPayMode;
  employeeHourlyRate?: number | null;
  clientHourlyRate?: number | null;
  employeeFixedAmount?: number | null;
  clientFixedAmount?: number | null;
}): Promise<void> {
  const { error } = await supabase.rpc("approve_cleaning_extra_job", {
    p_job_id: input.jobId,
    p_pay_mode: input.payMode,
    p_employee_hourly_rate: input.employeeHourlyRate ?? undefined,
    p_client_hourly_rate: input.clientHourlyRate ?? undefined,
    p_employee_fixed_amount: input.employeeFixedAmount ?? undefined,
    p_client_fixed_amount: input.clientFixedAmount ?? undefined,
  });
  if (error) throw new Error(rpcErrorMessage(error, "Nie udało się zaakceptować zlecenia."));
}

export async function rejectExtraJob(jobId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc("reject_cleaning_extra_job", {
    p_job_id: jobId,
    p_reason: reason ?? undefined,
  });
  if (error) throw new Error(rpcErrorMessage(error, "Nie udało się odrzucić zlecenia."));
}

export async function uploadExtraJobPhoto(input: {
  orgId: string;
  jobId: string;
  userId: string;
  file: File;
  source: ExtraJobPhotoSource;
  sortOrder: number;
}): Promise<void> {
  const compressed = await compressImage(input.file, { maxWidthOrHeight: 1600, initialQuality: 0.7 });
  const photoId = crypto.randomUUID();
  const path = `extra-jobs/${input.orgId}/${input.jobId}/${photoId}.jpg`;
  const { error: uploadError } = await supabase.storage.from("photos").upload(path, compressed, {
    contentType: "image/jpeg",
    upsert: false,
  });
  if (uploadError) {
    console.error("[uploadExtraJobPhoto] storage", uploadError);
    throw new Error("Nie udało się wgrać zdjęcia.");
  }
  const { error } = await supabase.from("cleaning_extra_job_photos").insert({
    org_id: input.orgId,
    extra_job_id: input.jobId,
    uploaded_by: input.userId,
    storage_path: path,
    source: input.source,
    sort_order: input.sortOrder,
  });
  if (error) {
    console.error("[uploadExtraJobPhoto] row", error);
    throw new Error("Nie udało się zapisać zdjęcia.");
  }
}

export async function extraJobPhotoUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("photos").createSignedUrl(storagePath, 3600);
  if (error) {
    console.warn("[extraJobPhotoUrl]", error);
    return null;
  }
  return data.signedUrl;
}
